import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import CompanyBasicInfo from "./CompanyBasicInfo";
import { INIT_STEPS } from "../../services/constants/InitialConstants";
import Address from "../../common/Address";
import CompanyDetails from "./CompanyDetails";
import { IAddress, ICompanyInfo } from "../../services/types/LeadRequisitionType";
import FooterButtonHoc from "../../common/FooterButtonHoc";
import { Layout } from "@citi-icg-172888/icgds-patterns-react";
import { useData } from "../../services/context/DataContext";
import { IStep } from "../../types/commonTypes";
import { validateSaleSize, validateInputField } from "../../services/utils/utils";
import _ from "lodash";
import CustomAlert from "./CustomAlert";
import { handleScript } from "../../services/utils/utils";

interface ICompanyInfoProps {
  setIsAddCompanyInfo: (isAddCompanyInfo: boolean) => void;
}
const CompanyInfo: React.FC<ICompanyInfoProps> = ({ setIsAddCompanyInfo }) => {
  const {
    steps,
    setSteps,
    companyInfo,
    setCompanyInfo,

    staticData,
    setIsBasicInfo,
    setIsAboutBusiness,
    setError,
    setIsEditMode,
    setIsCancelClicked,
  } = useData();
  const countries = _.keys(staticData.countries);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [touchedFields, setTouchedFields] = useState<Partial<Record<string, boolean>>>({});
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);
  const divMainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    divRef.current?.focus();
    if (window.innerWidth < 768 && window.parent.location.href.includes("contact-us")) {
      window.parent.scroll(0, 0);
    } else if (window.parent.location.href.includes("contact-us")) {
      window.parent.scroll({ top: 0, behavior: "smooth" });
    }
  }, []);

  const validateFields = (
    updatedCompanyData: ICompanyInfo | null,
    field?: string,
    updatedTouchedFields = touchedFields
  ) => {
    const newErrors: Partial<Record<string, string>> = { ...errors };

    if (
      updatedTouchedFields.legalNameOfBusiness &&
      !validateInputField(updatedCompanyData?.legalNameOfBusiness)
    ) {
      newErrors.business = "Enter a registered company name.";
    } else delete newErrors.business;

    if (
      updatedTouchedFields.streetName &&
      !validateInputField(updatedCompanyData?.registerAddress?.streetName)
    ) {
      newErrors.street = "Please enter street name";
    } else delete newErrors.street;

    if (
      updatedTouchedFields.city &&
      !validateInputField(updatedCompanyData?.registerAddress?.city)
    ) {
      newErrors.city = "Enter city (no numbers or special characters allowed)";
    } else delete newErrors.city;

    if (
      updatedTouchedFields.state &&
      !validateInputField(updatedCompanyData?.registerAddress?.state)
    ) {
      newErrors.state = "Please enter State / Province name";
    } else delete newErrors.state;

    if (
      updatedTouchedFields.zipcode &&
      !validateInputField(updatedCompanyData?.registerAddress?.zipcode, "zipcode")
    ) {
      newErrors.zip = "Zip/Postal Code of Headquarters location (20 characters max)";
      if (
        updatedCompanyData?.registerAddress?.zipcode?.length === 0 ||
        (updatedCompanyData?.registerAddress?.zipcode?.length ?? 0) > 20
      ) {
        newErrors.zip = "Zip/Postal Code of Headquarters location (20 characters max)";
      }
    } else delete newErrors.zip;

    if (updatedTouchedFields.industry && !validateInputField(updatedCompanyData?.industry)) {
      newErrors.industryType = "Please select industry type";
    } else delete newErrors.industryType;

    if (updatedTouchedFields.salesSize && !validateInputField(updatedCompanyData?.salesSize)) {
      newErrors.salesSize = "Please select annual revenue";
    } else delete newErrors.salesSize;

    if (
      companyInfo?.salesSize &&
      updatedTouchedFields.isCompanyVCorPEBacked &&
      !validateInputField(updatedCompanyData?.isCompanyVCorPEBacked)
    ) {
      newErrors.vcPe = "Please select any";
    } else delete newErrors.vcPe;

    const lowerCaseUrl = updatedCompanyData?.website?.toLowerCase() ?? "";
    if (updatedTouchedFields.website && !validateInputField(lowerCaseUrl, "website")) {
      newErrors.website = "Please enter a valid Company Website";
    } else delete newErrors.website;

    setErrors(newErrors);
    return _.isEmpty(newErrors);
  };
  
  const onSubmitHandler = () => {
    setIsSubmitClicked(true);
    if (enabledSubmit) {
      const currentStep = 1;
      const nextStep = currentStep + 1;
      const newSteps = steps.map((step: IStep) => ({
        ...step,
        completed: step.step === currentStep,
        active: step.step === nextStep,
      }));
      handleScript(divMainRef, "ccbContactUsFormStep1CompanyInfoCompleted");
      setSteps(newSteps);
      setErrors({});
    } else {
      setTouchedFields(() => {
        let newTouchedFields = {
          legalNameOfBusiness: true,
          streetName: true,
          city: true,
          state: true,
          zipcode: true,
          industry: true,
          salesSize: true,
          website: true,
          isCompanyVCorPEBacked: false,
        };
        if (companyInfo?.salesSize) {
          newTouchedFields = { ...newTouchedFields, isCompanyVCorPEBacked: true };
        }
        validateFields(companyInfo, undefined, newTouchedFields);
        return newTouchedFields;
      });
    }
    divRef.current?.focus();
    if (window.parent.location.href.includes("contact-us"))
      divRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const onChangeCompanyData = useCallback(
    (data: ICompanyInfo, field: string) => {
      const updatedCompanyData = {
        businessInfo: companyInfo?.businessInfo,
        ...data,
      } as ICompanyInfo;

      setTouchedFields((prev) => {
        const newTouchedFields = { ...prev, [field]: true };
        validateFields(updatedCompanyData, field, newTouchedFields);
        return newTouchedFields;
      });
      setCompanyInfo(updatedCompanyData);
    },
    [companyInfo, setCompanyInfo]
  );

  const onChangeAddress = useCallback(
    (data: IAddress, field: string, propName: string) => {
      console.log("data: ", data, field, propName);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const country = propName === "registerAddress" && data.country ? data.country : null;
      let newArray = [];
      if (companyInfo?.operatedCountry) {
        newArray = companyInfo?.operatedCountry;
        const isCountryPresent = _.includes(
          companyInfo?.operatedCountry,
          companyInfo.headquaterCountry
        );
        if (isCountryPresent) {
          newArray = _.filter(newArray, (c) => c !== companyInfo.headquaterCountry);
          country && newArray.unshift(country);
        } else {
          country && newArray.unshift(country);
        }
      } else {
        newArray = [country];
      }

      const updatedCompanyData = {
        ...companyInfo,
        [propName]: data,
        ...(country && {
          headquaterCountry: country,
          operatedCountry: newArray,
        }),
      } as ICompanyInfo;

      setTouchedFields((prev) => {
        const newTouchedFields = { ...prev, [field]: true };
        validateFields(updatedCompanyData, field, newTouchedFields);
        return newTouchedFields;
      });
      updatedCompanyData && setCompanyInfo(updatedCompanyData);
    },
    [companyInfo, setCompanyInfo]
  );

  const onBackHandler = () => {
    setIsAddCompanyInfo(false);
  };

  const onCancelHandler = async () => {
    setSteps(INIT_STEPS);
    setIsAboutBusiness(false);
    setIsEditMode(false);
    setCompanyInfo(null);
    setIsBasicInfo(false);
    setError(null);
    setIsAddCompanyInfo(false);
    setIsCancelClicked(true);
  };

  const enabledSubmit = useMemo(() => {
    return (
      companyInfo?.legalNameOfBusiness &&
      companyInfo?.registerAddress?.streetName &&
      companyInfo?.registerAddress?.city &&
      companyInfo?.registerAddress?.state &&
      companyInfo?.registerAddress?.zipcode &&
      companyInfo?.industry &&
      validateInputField(companyInfo?.website?.toLocaleLowerCase(), "website") &&
      validateSaleSize(companyInfo)
    );
  }, [companyInfo]);

  const handleAlert = () => {
    setIsSubmitClicked(!isSubmitClicked);
  };

  return (
    <div className="ccbDncaWrapper lmn-container content-container">
      <div ref={divMainRef}></div>
      <div className="lmn-dnca-w-l-100 lmn-dnca-w-m-100 lmn-dnca-w-s-100" ref={divRef}>
        {isSubmitClicked && Object.values(errors).length > 0 && (
          <CustomAlert
            id="companyInfo-alert"
            errors={Object.values(errors)}
            handleChange={handleAlert}
            isSubmitClicked={isSubmitClicked}
          />
        )}
      </div>
      <CompanyBasicInfo
        companyInfo={companyInfo}
        id="basic_info"
        errorBusiness={errors.business}        
        onChange={(data: ICompanyInfo, field: string) => onChangeCompanyData(data, field)}
      />
      <Layout
        type="divider"
        spacingBottom="md"
        spacingTop="md"
        layoutClass="cust-border-color"
      ></Layout>
      <Address
        className=" lmn-dnca-w-s-100 lmn-dnca-w-m-100"
        title="Headquarters Information"
        data={companyInfo?.registerAddress || null}
        companyInfo={companyInfo}
        countries={countries}
        onChange={(data, field: string) => onChangeAddress(data, field, "registerAddress")}
        errorStreet={errors.street || ""}
        errorState={errors.state || ""}
        errorCity={errors.city || ""}
        errorZip={errors.zip || ""}
      />
      <Layout
        type="divider"
        spacingBottom="md"
        spacingTop="md"
        layoutClass="cust-border-color"
      ></Layout>
      <CompanyDetails
        className=" lmn-dnca-w-s-100 lmn-dnca-w-m-100"
        companyInfo={companyInfo}
        onChange={(data: ICompanyInfo, field: string) => onChangeCompanyData(data, field)}
        errorSalesSize={errors.salesSize || ""}
        errorIndustryType={errors.industryType || ""}
        errorVcPe={errors.vcPe || ""}
        errorWebSite={errors.website || ""}
      />
      <div>
        <FooterButtonHoc
          submitText="Continue"
          onBack={onBackHandler}
          onCancel={onCancelHandler}
          onSubmit={onSubmitHandler}
        />
      </div>
    </div>
  );
};

export default React.memo(CompanyInfo);
